import {
  getCustomerInfo,
  getProfileAddresses,
  getProfileContract,
  getProfileEmails,
  getProfileMobiles,
  getSessionLogs,
  getSessionLogsExportExcel,
  sejamInformation,
  getContractPhoneNumber,
  getContractByTypeCode,
  getSignedContractById,
} from '@/app/api/profiles/services';
import { ProblemDetailType } from '@/lib/utils/api';
import {
  IContract,
  IProfileAddresses,
  IProfileContract,
  IProfileEmails,
  IProfileMobiles,
  IProfileSecurityLogs,
  ISejamInfo,
  IContractPhoneNumber,
  ContractPreviewType,
} from '@/models/profiles';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';

export const useGetIndividualCustomer = (enabled: boolean) => {
  return useQuery({
    queryKey: ['individual-customer-header'],
    queryFn: () => getCustomerInfo(),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const useSejamInfo = () => {
  return useQuery<ISejamInfo, ProblemDetailType>({
    queryKey: ['sejam-info'],
    queryFn: () => sejamInformation(),
    refetchOnWindowFocus: false,
  });
};

export const useProfileMobiles = () => {
  return useQuery<IProfileMobiles, ProblemDetailType>({
    queryKey: ['profile-mobiles'],
    queryFn: () => getProfileMobiles(),
    select: (data) => {
      data?.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        if (a.isSejami && !b.isSejami) return -1;
        if (!a.isSejami && b.isSejami) return 1;
        return 0;
      });
      return data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useProfileEmails = () => {
  return useQuery<IProfileEmails>({
    queryKey: ['profile-emails'],
    queryFn: () => getProfileEmails(),
    refetchOnWindowFocus: false,
  });
};

export const useProfileAddresses = () => {
  return useQuery<IProfileAddresses>({
    queryKey: ['profile-addresses'],
    queryFn: () => getProfileAddresses(),
    refetchOnWindowFocus: false,
  });
};

export const useCustomerInfo = () => {
  return useQuery({
    queryKey: ['individual-customer'],
    queryFn: () => getCustomerInfo(),
    refetchOnWindowFocus: false,
  });
};

export const useLogsList = () => {
  return useQuery<IProfileSecurityLogs>({
    queryKey: ['profile-session-logs'],
    queryFn: () => getSessionLogs(),
    refetchOnWindowFocus: false,
  });
};

export const useExportLogsList = () => {
  return useQuery<Blob>({
    queryKey: ['profile-session-logs-excel-export'],
    queryFn: () => getSessionLogsExportExcel(),
    refetchOnWindowFocus: false,
    enabled: false,
  });
};

export const useProfileContract = () => {
  return useQuery<IProfileContract>({
    queryKey: ['profile-contract'],
    queryFn: () => getProfileContract(),
    refetchOnWindowFocus: false,
  });
};

export const useGetUnSignedContractByTypeCode = (
  contractTypeCodes: number[] | null,
  previewType: ContractPreviewType,
  options?: Partial<UseQueryOptions<IContract, ProblemDetailType>>
) => {
  return useQuery<IContract, ProblemDetailType>({
    queryKey: contractTypeCodes?.length
      ? ['unsigned-contract-by-code', ...contractTypeCodes]
      : ['unsigned-contract-by-code'],
    queryFn: () => {
      if (!contractTypeCodes?.length) {
        return Promise.reject(new Error('Missing contractTypeCodes'));
      }
      return getContractByTypeCode(contractTypeCodes, previewType);
    },
    enabled: !!contractTypeCodes?.length && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useGetSignedContractById = (
  contractId: number | null,
  options?: Partial<UseQueryOptions<Blob, ProblemDetailType>>
) => {
  return useQuery<Blob, ProblemDetailType>({
    queryKey:
      contractId !== null ? ['signed-contract-by-id', contractId] : ['signed-contract-by-id'],
    queryFn: async () => {
      const result = await getSignedContractById(contractId!);

      if (result instanceof Blob) return result;
      if ('data' in result && result.data instanceof Blob) return result.data;
      throw {
        status: 500,
        type: 'unexpected_response',
        detail: 'Unexpected file response',
        errors: [
          {
            code: '100',
            message: 'خطایی رخ داده است لطفا بعدا تلاش کنید.',
          },
        ],
      } satisfies ProblemDetailType;
    },
    enabled: contractId !== null && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useGetContractPhoneNumber = (enabled: boolean) => {
  return useQuery<IContractPhoneNumber>({
    queryKey: ['Contract-PhoneNumber'],
    queryFn: () => getContractPhoneNumber(),
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};
